import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Minus, Trash2, ShoppingCart, Layers } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CustomizeIngredientsDialog from './CustomizeIngredientsDialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AddOrderDialog({ rental, open, onClose, onOrderSuccess }) {
  const [additionalItems, setAdditionalItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [customizingProduct, setCustomizingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [removingItemIndex, setRemovingItemIndex] = useState(null);

  // Fetch products and reset form when dialog opens
  useEffect(() => {
    if (open) {
      fetchProducts();
      // Reset form saat dialog dibuka
      setAdditionalItems([]);
      setSelectedProduct('');
      setNewItemQty(1);
      setSearchTerm('');
    }
  }, [open]);

  // Lock body scroll when modal is open & scroll to top
  useEffect(() => {
    if (open) {
      // Scroll to top of page to show modal
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('Fetching products from:', `${API}/api/products`);
      
      const response = await axios.get(`${API}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Products response:', response.data);
      
      if (response.data.success) {
        setProducts(response.data.products);
        console.log(`Loaded ${response.data.products.length} products`);
      } else {
        console.error('Products fetch unsuccessful:', response.data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      console.error('Error detail:', error.response?.data);
      // Don't show error toast, might be CORS issue but products exist
      // Just set empty array so user can still use manual input
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const totalAmount = additionalItems.reduce((sum, item) => sum + (item.price * item.qty), 0);

  const handleAddItem = () => {
    if (!selectedProduct) {
      toast.error('Pilih produk terlebih dahulu!');
      return;
    }

    const product = products.find(p => p.id === selectedProduct);
    if (!product) {
      toast.error('Produk tidak ditemukan!');
      return;
    }

    const qty = parseInt(newItemQty) || 1;

    // For composite products, add with default recipe (standard order)
    if (product.type === 'composite') {
      const item = {
        id: Date.now(),
        product_id: product.id,
        name: product.name,
        price: product.price,
        qty: qty,
        type: 'composite',
        custom_recipe: product.recipe || [] // Use default recipe
      };

      setAdditionalItems([...additionalItems, item]);
      setSelectedProduct('');
      setNewItemQty(1);
      toast.success('Item ditambahkan (resep standar)!');
      return;
    }

    // Simple product - check stock
    if (product.stock < qty) {
      toast.error(`Stok tidak cukup! Tersedia: ${product.stock}`);
      return;
    }

    const item = {
      id: Date.now(),
      product_id: product.id,
      name: product.name,
      price: product.price,
      qty: qty,
      type: 'simple'
    };

    setAdditionalItems([...additionalItems, item]);
    setSelectedProduct('');
    setNewItemQty(1);
    toast.success('Item ditambahkan!');
  };

  const handleCustomizeClick = () => {
    if (!selectedProduct) {
      toast.error('Pilih produk terlebih dahulu!');
      return;
    }

    const product = products.find(p => p.id === selectedProduct);
    if (!product) {
      toast.error('Produk tidak ditemukan!');
      return;
    }

    if (product.type !== 'composite') {
      toast.error('Hanya produk komposit yang bisa di-customize!');
      return;
    }

    setCustomizingProduct(product);
  };

  const handleCustomizeConfirm = (customizedProduct) => {
    const qty = parseInt(newItemQty) || 1;
    
    const item = {
      id: Date.now(),
      product_id: customizedProduct.id,
      name: customizedProduct.name,
      price: customizedProduct.custom_price,
      qty: qty,  // Use quantity from input
      type: 'composite',
      custom_recipe: customizedProduct.custom_recipe
    };

    setAdditionalItems([...additionalItems, item]);
    setCustomizingProduct(null);
    setSelectedProduct('');
    setNewItemQty(1);  // Reset quantity
    toast.success(`${qty}x ${item.name} ditambahkan!`);
  };

  const handleRemoveItem = (id) => {
    setAdditionalItems(additionalItems.filter(item => item.id !== id));
  };

  const handleRemoveExistingItem = async (itemIndex) => {
    if (!window.confirm('Hapus item ini? Stok akan dikembalikan.')) {
      return;
    }

    setRemovingItemIndex(itemIndex);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/api/rentals/${rental.id}/remove-order-item`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: { item_index: itemIndex }
        }
      );

      if (response.data.success) {
        toast.success('Item berhasil dihapus dan stok dikembalikan!');
        
        // Refresh rental data by calling parent's refresh function
        // The parent component should re-fetch the rental data
        if (onOrderSuccess) {
          // Call with null to trigger refresh without adding new items
          await onOrderSuccess(rental.id, null, true);
        }
      } else {
        toast.error(response.data.error || 'Gagal menghapus item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Gagal menghapus item';
      toast.error(errorMsg);
    } finally {
      setRemovingItemIndex(null);
    }
  };

  const handleSubmit = async () => {
    if (additionalItems.length === 0) {
      toast.error('Tambahkan minimal 1 item!');
      return;
    }

    setProcessing(true);
    try {
      await onOrderSuccess(rental.id, additionalItems);
      
      // RESET FORM FIRST before closing
      setAdditionalItems([]);
      setSelectedProduct('');
      setNewItemQty(1);
      setSearchTerm('');
      
      // Show success and close
      toast.success('Pesanan berhasil ditambahkan!');
      onClose();
    } catch (error) {
      console.error('Error adding order:', error);
      // Show more specific error message if available
      const errorMsg = error.response?.data?.detail || error.message || 'Gagal menambahkan pesanan';
      toast.error(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (!open) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modern Modal Container - Consistent with PrepaidRentalDialog */}
      <div className="bg-white rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto scrollbar-hide flex flex-col">
        {/* Header - Clean & Modern */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-gray-900">Tambah Pesanan</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            {rental?.customer_name} • {rental?.ps_unit_name}
          </p>
        </div>

        {/* Body - Clean Form Layout */}
        <div className="flex-1 p-6 space-y-4">
          {/* Info text */}
          <div className="text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
             Tambahkan makanan/minuman yang dipesan customer. Total akan ditambahkan ke tagihan rental.
          </div>

          {/* Current Items Display */}
          {rental?.additional_items && rental.additional_items.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <h4 className="text-sm font-semibold text-gray-800 mb-2">
                 Items Saat Ini
              </h4>
              <div className="space-y-2">
                {rental.additional_items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between bg-white border border-orange-300 rounded-lg p-2">
                    <div className="flex-1">
                      <p className="font-medium text-gray-800 text-sm">{item.name} × {item.qty}</p>
                      <p className="text-xs text-gray-600">
                        Rp {item.price.toLocaleString('id-ID')} per item
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-800 text-sm">
                        Rp {(item.price * item.qty).toLocaleString('id-ID')}
                      </span>
                      <button
                        onClick={() => handleRemoveExistingItem(index)}
                        disabled={removingItemIndex === index}
                        className="text-red-500 hover:text-red-600 p-1 hover:bg-red-50 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Hapus item (stok akan dikembalikan)"
                      >
                        {removingItemIndex === index ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent"></div>
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-orange-300 flex justify-between text-sm font-semibold text-orange-700">
                <span>Subtotal F&B:</span>
                <span>
                  Rp {rental.additional_items.reduce((sum, item) => sum + (item.price * item.qty), 0).toLocaleString('id-ID')}
                </span>
              </div>
            </div>
          )}

          {/* Search Products */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
               Cari Produk F&B
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
              placeholder="Ketik nama produk atau kategori..."
            />
          </div>

          {/* Product Selection */}
          {loadingProducts ? (
            <div className="text-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Memuat produk...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-6 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-700 mb-1"> Belum ada produk yang tersedia</p>
              <p className="text-xs text-yellow-600">
                Admin belum menambahkan produk ke katalog
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                   Pilih Produk *
                </label>
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                >
                  <option value="">-- Pilih Produk --</option>
                  {products
                      .filter(product => 
                        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        product.category.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.name} - Rp {product.price.toLocaleString('id-ID')} 
                          {product.type === 'composite' ? ' (Komposit)' : ` (Stok: ${product.stock})`}
                        </option>
                      ))}
                  </select>
                  {searchTerm && (
                    <p className="text-xs text-gray-500 mt-1">
                       Menampilkan {products.filter(p => 
                        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        p.category.toLowerCase().includes(searchTerm.toLowerCase())
                      ).length} dari {products.length} produk
                    </p>
                  )}
                </div>
                
                {selectedProduct && (() => {
                  const selectedProd = products.find(p => p.id === selectedProduct);
                  return (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">
                             {selectedProd?.name}
                          </p>
                          <p className="text-xs text-gray-600">
                            Kategori: {selectedProd?.category}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            Rp {selectedProd?.price.toLocaleString('id-ID')}
                          </p>
                          <p className="text-xs text-gray-600">
                            {selectedProd?.type === 'composite' ? 'Komposit' : `Stok: ${selectedProd?.stock}`}
                          </p>
                        </div>
                      </div>
                      
                      {/* Show ingredients for composite products */}
                      {selectedProd?.type === 'composite' && selectedProd?.recipe && selectedProd.recipe.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-green-300">
                          <p className="text-xs font-semibold text-gray-700 mb-2"> Bahan yang dibutuhkan:</p>
                          <div className="space-y-1">
                            {selectedProd.recipe.map((ing, idx) => {
                              // Display infinity icon for non-stock ingredients
                              if (ing.is_non_stock) {
                                return (
                                  <div key={idx} className="flex items-center justify-between text-xs">
                                    <span className="text-gray-700">
                                      • {ing.name} ({ing.quantity} {ing.unit})
                                    </span>
                                    <span className="text-blue-600 font-medium">
                                       Unlimited
                                    </span>
                                  </div>
                                );
                              }
                              
                              const isLowStock = ing.stock < 10;
                              const isOutOfStock = ing.stock <= 0;
                              return (
                                <div key={idx} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-700">
                                    • {ing.name} ({ing.quantity} {ing.unit})
                                  </span>
                                  <span className={`font-medium ${
                                    isOutOfStock ? 'text-red-600' : 
                                    isLowStock ? 'text-amber-600' : 
                                    'text-green-600'
                                  }`}>
                                    {isOutOfStock ? ' Habis' : isLowStock ? ` ${ing.stock}` : ` ${ing.stock}`}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

              {/* Quantity and Action Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                   Jumlah
                </label>
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => setNewItemQty(Math.max(1, parseInt(newItemQty || 1) - 1))}
                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={newItemQty}
                    onChange={(e) => setNewItemQty(e.target.value)}
                    min="1"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-center font-semibold focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                  />
                  <button
                    type="button"
                    onClick={() => setNewItemQty(parseInt(newItemQty || 1) + 1)}
                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Action Buttons */}
                <div className={`grid gap-2 ${selectedProduct && products.find(p => p.id === selectedProduct)?.type === 'composite' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {/* Add Standard Button */}
                  <button
                    onClick={handleAddItem}
                    disabled={!selectedProduct}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1 ${
                      selectedProduct 
                        ? 'bg-gradient-to-r from-green-600 to-green-500 text-white hover:from-green-700 hover:to-green-600 hover:shadow-lg' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Plus className="h-4 w-4" />
                    Tambah
                  </button>
                  
                  {/* Customize Button (only for composite) */}
                  {selectedProduct && products.find(p => p.id === selectedProduct)?.type === 'composite' && (
                    <button
                      onClick={handleCustomizeClick}
                      className="px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600 hover:shadow-lg"
                    >
                      <Layers className="h-4 w-4" />
                      Customize
                    </button>
                  )}
                </div>
                
                {selectedProduct && products.find(p => p.id === selectedProduct)?.type === 'composite' && (
                  <p className="text-xs text-gray-500 mt-1">
                     Klik "Tambah" untuk resep standar, atau "Customize" untuk ubah komposisi
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Selected Items List */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                 Daftar Pesanan Baru
              </h3>
              <span className="text-xs bg-gray-200 px-2 py-1 rounded-full text-gray-600">
                {additionalItems.length} item
              </span>
            </div>

            {additionalItems.length > 0 ? (
              <div className="space-y-2">
                {additionalItems.map((item) => (
                  <div key={item.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{item.name}</p>
                      <p className="text-sm text-gray-600">
                        Rp {item.price.toLocaleString('id-ID')} × {item.qty}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-gray-800">
                        Rp {(item.price * item.qty).toLocaleString('id-ID')}
                      </span>
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-red-500 hover:text-red-600 p-1 hover:bg-red-50 rounded transition"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 font-medium">Belum ada pesanan baru</p>
                <p className="text-gray-400 text-sm mt-1">Pilih produk di atas untuk menambahkan</p>
              </div>
            )}
          </div>

          {/* Total Section */}
          {additionalItems.length > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Total Pesanan Baru</p>
                  <p className="text-xs text-gray-500">{additionalItems.length} item akan ditambahkan</p>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  Rp {totalAmount.toLocaleString('id-ID')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-2 justify-end flex-shrink-0">
          <button
            onClick={onClose}
            disabled={processing}
            className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200 hover:border-gray-400 font-semibold rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={processing || additionalItems.length === 0}
            className={`px-6 py-2 font-semibold rounded-lg transition ${
              processing || additionalItems.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                : 'bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white hover:shadow-lg transform hover:scale-105'
            }`}
          >
            {processing ? 'Memproses...' : `Simpan Pesanan (Rp ${totalAmount.toLocaleString('id-ID')})`}
          </button>
        </div>
      </div>

      {/* Customize Ingredients Dialog */}
      {customizingProduct && (
        <CustomizeIngredientsDialog
          product={customizingProduct}
          onConfirm={handleCustomizeConfirm}
          onClose={() => setCustomizingProduct(null)}
        />
      )}
    </div>
  );

  // Render modal using Portal to ensure it's at document body level
  return createPortal(modalContent, document.body);
}
